import ShopifyBulkOperationsService from '../services/shopifyBulkOperationsService';
import { hasEnoughCredits, deductCreditsAndRecordPush } from '../services/creditService';

/**
 * Handlers for API operations (fetch, save, push to Shopify)
 */

export const createApiHandlers = (
  currentShop,
  user,
  shopifyBulkOperationsService
) => {
  /**
   * Fetch products from Shopify API
   */
  const handleFetchProducts = async (
    shop,
    productIds,
    setLoading,
    setError,
    setProducts,
    setLoadingProgress,
    fetchedIdsRef = null
  ) => {
    if (!shop?.shop) {
      console.log('No shop, skipping fetch');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError('');
      console.log('Starting product fetch for IDs:', productIds);

      const bulkService = new ShopifyBulkOperationsService(
        shop.shop,
        shop.accessToken
      );
      
      const query = ShopifyBulkOperationsService.buildFullProductQueryForIds(productIds);
      let bulkOperation;
      try {
        bulkOperation = await bulkService.startBulkProductsQuery(query);
      } catch (error) {
        // If there's already a bulk operation running, wait for it to complete
        if (error.message.includes('already in progress')) {
          console.log('Bulk operation already in progress, waiting for completion...');
          // Extract operation ID from error message
          const match = error.message.match(/gid:\/\/shopify\/BulkOperation\/\d+/);
          if (match) {
            const operationId = match[0];
            console.log('Waiting for operation:', operationId);
            const results = await bulkService.pollBulkOperation(operationId);
            if (results) {
              bulkOperation = { id: operationId };
            } else {
              throw new Error('Failed to get results from existing bulk operation');
            }
          } else {
            throw new Error('Could not extract operation ID from error message');
          }
        } else {
          throw error;
        }
      }
      
      if (!bulkOperation?.id) {
        throw new Error('Failed to start bulk operation - no operation ID received');
      }
      
      const jsonlData = await bulkService.pollBulkOperation(bulkOperation.id);
      console.log('Raw results from API:', jsonlData);
      
      if (!jsonlData || typeof jsonlData !== 'string' || jsonlData.trim() === '') {
        console.error('No valid JSONL data from bulk operation');
        setProducts([]);
        setError('No products found - please check the product IDs and try again');
        return;
      }
      
      // Process the JSONL data to get an array of products
      const results = bulkService.processProductsData(jsonlData);
      
      if (!results || !Array.isArray(results) || results.length === 0) {
        console.error('No valid results after processing bulk operation data');
        setProducts([]);
        setError('No products found - please check the product IDs and try again');
        return;
      }

      // Validate that we have actual product data
      const validProducts = results.filter(product => 
        product && 
        product.id && 
        product.title &&
        product.id.includes('gid://shopify/Product/')
      );

      if (validProducts.length === 0) {
        console.error('No valid product nodes found in results');
        setProducts([]);
        setError('No valid products found in the results');
        return;
      }

      // Try to enrich products with metafield definitions, but fall back to original products if it fails
      let enrichedProducts;
      try {
        enrichedProducts = await bulkService.enrichProductsWithMetafieldDefinitions(validProducts);
        console.log('Products enriched with metafield definitions:', enrichedProducts);
      } catch (error) {
        console.warn('Failed to enrich products with metafield definitions, using original products:', error);
        enrichedProducts = validProducts;
      }

      // Process the products
      const processedProducts = enrichedProducts.map(product => {
        // Map variants to include complete data including images
        const variants = product.variants.map(variant => ({
          id: variant.id,
          title: variant.title,
          sku: variant.inventoryItem?.sku || '',
          price: variant.price || "0.00",
          compareAtPrice: variant.compareAtPrice,
          inventoryQuantity: variant.inventoryQuantity || 0,
          inventory_quantity: variant.inventoryQuantity || 0, // Add for compatibility
          quantity: variant.inventoryQuantity || 0, // Add for compatibility
          selectedOptions: variant.selectedOptions,
          optionValues: variant.selectedOptions?.map(opt => opt.value) || [], // Add for direct access
          position: variant.position,
          inventoryItem: variant.inventoryItem,
          barcode: variant.barcode || '',
          taxable: variant.taxable,
          // Store image data directly with the variant
          image: variant.image,
          // Add any other fields needed for variant creation
        }));

        // Process product metafields
        const metafields = (product.metafields || []);

        return {
          ...product,
          variants,
          // Store the original variants for reference
          originalVariants: JSON.parse(JSON.stringify(variants)),
          metafields,
          isExpanded: false
        };
      });

      console.log('Processed products:', processedProducts);
      setProducts(processedProducts);
      setError('');
      setLoadingProgress({ current: processedProducts.length, total: productIds.length });
      
      // Update the fetchedIdsRef after successful completion if provided
      if (fetchedIdsRef && productIds.length > 0) {
        const currentIdsString = [...productIds].sort().join(',');
        fetchedIdsRef.current = currentIdsString;
        console.log('Updated fetchedIdsRef with:', currentIdsString);
      }
    } catch (error) {
      console.error('Error fetching products:', error);
      setError(`Failed to load products: ${error.message}`);
      setProducts([]);
    } finally {
      setLoading(false);
      setLoadingProgress({ current: 0, total: 0 });
    }
  };

  /**
   * Push changes to Shopify
   */
  const handlePushToShopify = async (
    shop,
    user,
    localEdits,
    products,
    setIsPushing,
    setSavingProgress,
    setLocalEdits,
    setHasUnsavedChanges,
    showToast,
    setError,
    fetchProductsCallback
  ) => {
    if (!shop || !user) return;

    setIsPushing(true);
    const totalProducts = Object.keys(localEdits).length;
    setSavingProgress({ current: 0, total: totalProducts, message: '' });

    try {
      // Check if user has enough credits first
      const productsToUpdate = totalProducts;
      const hasCredits = await hasEnoughCredits(user.uid, productsToUpdate);
      
      if (!hasCredits) {
        showToast(`Not enough credits. You need ${productsToUpdate} credits to update these products.`, true);
        setIsPushing(false);
        return;
      }

      console.log('Credit check passed, proceeding with updates...');

      let successCount = 0;
      let errorCount = 0;
      const errors = [];

      const bulkService = new ShopifyBulkOperationsService(
        shop.shop,
        shop.accessToken
      );

      // Process each product atomically
      for (const [productId, changes] of Object.entries(localEdits)) {
        try {
          setSavingProgress(prev => ({
            ...prev,
            message: `Updating product ${successCount + errorCount + 1} of ${totalProducts}`
          }));

          // Update the product with all changes
          await bulkService.updateProduct(productId, changes);
          
          successCount++;
          setSavingProgress(prev => ({
            ...prev,
            current: successCount + errorCount
          }));
        } catch (error) {
          console.error(`Error updating product ${productId}:`, error);
          errors.push(`Failed to update product ${productId}: ${error.message}`);
          errorCount++;
          setSavingProgress(prev => ({
            ...prev,
            current: successCount + errorCount
          }));
        }
      }

      // Deduct credits for successful updates
      if (successCount > 0) {
        await deductCreditsAndRecordPush(
          user.uid,
          successCount,
          shop.shop
        );
      }

      // Show results
      if (errorCount === 0) {
        showToast(`Successfully updated ${successCount} products`);
        setLocalEdits({});
        setHasUnsavedChanges(false);
        
        // Refresh products to show updated data
        if (fetchProductsCallback) {
          const productIds = products.map(p => p.id);
          await fetchProductsCallback(
            shop,
            productIds,
            () => {},
            setError,
            () => {},
            () => {}
          );
        }
      } else {
        showToast(`Updated ${successCount} products with ${errorCount} errors`, true);
        console.error('Update errors:', errors);
      }
    } catch (error) {
      console.error('Error in push to Shopify:', error);
      showToast(`Error: ${error.message}`, true);
    } finally {
      setIsPushing(false);
      setSavingProgress({ current: 0, total: 0, message: '' });
    }
  };

  /**
   * Save changes locally
   */
  const handleSave = async (
    localEdits,
    setIsSaving,
    bulkService,
    showToast,
    setLocalEdits,
    setHasUnsavedChanges
  ) => {
    setIsSaving(true);
    try {
      const updates = [];
      
      // Collect all changes
      Object.entries(localEdits).forEach(([productId, changes]) => {
        updates.push({
          id: productId,
          ...changes // This now directly contains field: value pairs
        });
      });

      if (updates.length > 0) {
        await bulkService.updateProducts(updates);
        showToast('Changes saved successfully');
        setLocalEdits({}); // Clear local edits after saving
        setHasUnsavedChanges(false);
      }
    } catch (error) {
      console.error('Error saving changes:', error);
      showToast('Failed to save changes', true);
    } finally {
      setIsSaving(false);
    }
  };

  /**
   * Make a generic API request
   */
  const handleApiRequest = async (endpoint, data, method = 'POST') => {
    if (!currentShop) {
      throw new Error('No shop selected');
    }

    try {
      const response = await fetch(`/api/${endpoint}`, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'X-Shop': currentShop.shop,
          'X-Access-Token': currentShop.accessToken
        },
        body: JSON.stringify(data)
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`API request failed: ${errorText}`);
      }

      return await response.json();
    } catch (error) {
      console.error(`API request to ${endpoint} failed:`, error);
      throw error;
    }
  };

  /**
   * Make a file upload API request
   */
  const handleApiRequestWithFile = async (endpoint, formData) => {
    if (!currentShop) {
      throw new Error('No shop selected');
    }

    try {
      const response = await fetch(`/api/${endpoint}`, {
        method: 'POST',
        headers: {
          'X-Shop': currentShop.shop,
          'X-Access-Token': currentShop.accessToken
        },
        body: formData
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`API request failed: ${errorText}`);
      }

      return await response.json();
    } catch (error) {
      console.error(`API request to ${endpoint} failed:`, error);
      throw error;
    }
  };

  return {
    handleFetchProducts,
    handlePushToShopify,
    handleSave,
    handleApiRequest,
    handleApiRequestWithFile
  };
};
